import { MouseEventHandler, useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { Coverage, PolicyType } from "@graphql/api";

import { Button } from "@covertree/ui";

import TabAccordion from "@components/TabAccordion";
import useAppDispatch from "@redux/hooks/useAppDispatch";

import TllCoverageItemContent from "./content/TllCoverageItemContent";
import CoverageItemContent from "./content/CoverageItemContent";
import getCoverageItemTitle from "../../../utils/getCoverageItemTitle";
import {
  ADD_EVENT_ROUTES,
  BASE_COVERAGE_PATH,
  COVERAGE_ROUTES,
} from "@routing/Residents/ProofOfCoverage";
import { addCurrentExternalCoverageDataToStateForAdditionOfPolicyEvent } from "@redux/coverages/coveragesSlice";
import { setPreEventCoverage } from "@redux/event/eventSlice";
import { useAuthContext } from "@covertree/auth";
import { CognitoUserGroup } from "@enums";

interface Props {
  coverage: Coverage;
  isResidentExcluded: boolean;
  switchToDocuments(): void;
}

export default function CoverageItem({
  coverage,
  isResidentExcluded,
  switchToDocuments,
}: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { hasGroup } = useAuthContext();

  const [searchParams] = useSearchParams();

  const handleAddPolicyEvent: MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      dispatch(setPreEventCoverage(coverage));
      dispatch(
        addCurrentExternalCoverageDataToStateForAdditionOfPolicyEvent(coverage)
      );
      navigate(
        `${location.pathname}/${BASE_COVERAGE_PATH}/${coverage.id}/${COVERAGE_ROUTES.ADD_EVENT}/${ADD_EVENT_ROUTES.EVENT_TYPE}?${searchParams.toString()}`
      );
    },
    [dispatch, navigate, location.pathname, coverage, searchParams]
  );

  return (
    <TabAccordion
      id={`${coverage.id}-${coverage.policyType}`}
      title={getCoverageItemTitle(coverage)}
      endSlot={
        !isResidentExcluded &&
        coverage.policyType === PolicyType.External &&
        hasGroup([
          CognitoUserGroup.CoverTreeEmployee,
          CognitoUserGroup.CoverTreeExecutive,
        ]) ? (
          <Button
            variant="outlined"
            size="small"
            onClick={handleAddPolicyEvent}>
            + Add Policy Event
          </Button>
        ) : undefined
      }>
      <>
        {coverage.policyType === PolicyType.TLL && (
          <TllCoverageItemContent coverage={coverage} />
        )}
        {coverage.policyType !== PolicyType.TLL && (
          <CoverageItemContent
            coverage={coverage}
            switchToDocuments={switchToDocuments}
          />
        )}
      </>
    </TabAccordion>
  );
}
